/** @jsx jsx */
import IconClose from '@apollo/icons/default/IconClose.svg';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  customColors,
} from '@apollo/orbit';
import { TextField } from '@apollo/space-kit/TextField';
import { css, jsx } from '@emotion/react';
import React, { useState } from 'react';

import { Loading } from 'src/components/common/loading/Loading';

import { DescriptionMarkdownRenderer } from '../graph/documentationPage/schemaPage/descriptionMarkdownRenderer/DescriptionMarkdownRenderer';
import { ATLAS_EXPLORER_FLAGS } from '../graph/explorerPage/explorerBehavioralFlags';
import { useHeadersManagerContext } from '../graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { ATLAS_GRAPH_FLAGS } from '../graph/variantLayout/graphBehavioralFlags/graphBehavioralFlags';

const TOKEN_REPLACEMENT_TEXT = 'TOKEN';

const { text, bg } = customColors;

const ExplorerPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "ExplorerPage" */ '../graph/explorerPage/ExplorerPage'
    )
  ).ExplorerPage,
}));

export const AtlasGraphPeekModal = ({
  isOpen,
  onClose,
  defaultQuery,
  requiredHeader,
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultQuery?: string;
  requiredHeader?: {
    headerName: string;
    headerDescription: string;
    headerTemplate: string;
  };
}) => {
  const { tabbedHeaderDefinitions, setTabbedHeaderDefinitions } =
    useHeadersManagerContext();

  const prevReqHeaderValue = tabbedHeaderDefinitions.find(
    (h) => h.headerName === requiredHeader?.headerName,
  )?.value;
  const headerNegative = requiredHeader?.headerTemplate.replace(
    TOKEN_REPLACEMENT_TEXT,
    '',
  );
  const [headerValue, setHeaderValue] = useState(
    prevReqHeaderValue?.replace(headerNegative || '', ''),
  );
  const [headerConfirmed, setHeaderConfirmed] = useState(false);

  return requiredHeader && !headerConfirmed ? (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore jsx expression too complex  */}
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>This graph requires authentication</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <DescriptionMarkdownRenderer
            descriptionMarkdown={requiredHeader.headerDescription}
            typeMap={{}}
            css={css`
              a {
                color: ${text.link};
                text-decoration: underline;
                font-weight: semibold;
              }

              p {
                margin-bottom: 12px;
              }

              pre {
                background-color: ${bg.primary};
                font-size: 13px;
                padding: 8px 16px;
                border-radius: 4px;
                overflow-x: auto;
                margin-bottom: 12px;
              }
            `}
          />
          <TextField
            label={`${requiredHeader.headerName} Header`}
            value={headerValue}
            onChange={(e) => {
              setHeaderValue(e.target.value);
            }}
          />
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              type="submit"
              onClick={() => {
                const header = {
                  headerName: requiredHeader.headerName,
                  value: requiredHeader.headerTemplate.replace(
                    TOKEN_REPLACEMENT_TEXT,
                    headerValue || '',
                  ),
                  checked: true,
                  key: 1,
                };

                setTabbedHeaderDefinitions(
                  prevReqHeaderValue
                    ? tabbedHeaderDefinitions.map((h) =>
                        h.headerName === requiredHeader.headerName ? header : h,
                      )
                    : [...tabbedHeaderDefinitions, header],
                );
                setHeaderConfirmed(true);
              }}
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  ) : (
    <Modal size="6xl" isOpen={isOpen} onClose={onClose}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore jsx expression too complex  */}
      <ModalOverlay />
      <ModalContent className="bg-transparent">
        <ModalBody className="relative w-full max-w-7xl overflow-visible bg-transparent !p-0">
          <div
            className="absolute -right-4 -top-4 flex size-8 cursor-pointer items-center justify-center rounded-full border border-primary bg-white"
            onClick={onClose}
          >
            <IconClose className="text-icon-primary" />
          </div>
          <div
            style={{
              maxHeight: 1000,
              height: '80vh',
            }}
          >
            <React.Suspense fallback={<Loading />}>
              <ExplorerPage
                graphBehavioralFlags={ATLAS_GRAPH_FLAGS}
                explorerBehavioralFlags={{
                  ...ATLAS_EXPLORER_FLAGS,
                  ...(defaultQuery
                    ? {
                        overrideDefaultTabState: { operation: defaultQuery },
                      }
                    : {}),
                }}
              />
            </React.Suspense>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
